import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import React, { useState } from 'react';

import { useImmer } from 'use-immer';

import { Company, PhoneCallDetailDTO } from '_lib/api/apiTypes.shim';
import {
  getLeftHighlightWarnSx,
  getLeftHighlightOkSx,
  getBoxStyleForCanIncludeFee,
  getBoxStyleForCanWaiveFee
} from '_lib/callPop/common/CompanyPane/CompanyPane.styles';
import {
  createZoneZipSelectState,
  getZoneZipOptions,
  AutoSelectOption,
  zoneZipFilterOptions,
  findZoneZipMatch,
  ZIP_CODE_REGEX
} from '_lib/callPop/common/CompanyPane/CompanySelected/BaseCompanyZipZoneSelect';
import { FormattedContactBlock } from '_lib/callPop/common/CompanyPane/FormattedContactBlock/FormattedContactBlock';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { Autocomplete } from '_lib/components/Autocomplete/Autocomplete';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Markup } from '_lib/components/Markup/Markup';
import { ShowMoreText } from '_lib/components/ShowMoreText/ShowMoreText';
import { Stack } from '_lib/components/Stack/Stack';
import { Tab } from '_lib/components/Tab/Tab';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { Tabs } from '_lib/components/Tabs/Tabs';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { formatPhone, canOrCant } from '_lib/utils/helpers';

import { HeaderBox } from '../../HeaderBox/HeaderBox';
import { HeaderTextBlock } from '../../HeaderTextBlock/HeaderTextBlock';
import { IconTypography } from '../../IconTypography/IconTypography';
import { KeyValueGrid } from '../../KeyValueGrid/KeyValueGrid';
import { TextBlockTypography } from '../../TextBoxTypography/TextBoxTypography';
import { TradesDisplay } from '../../Trades/TradesDisplay';

const getZoneInfo = (phoneCall: PhoneCallDetailDTO, company?: Company) => {
  const zoneInfoPhoneCall = {
    companyIsZoned: !!phoneCall.companyIsZoned,
    companyZoneId: phoneCall.companyZoneId,
    companyZoneTitle: phoneCall.companyZoneTitle
  };

  if (!zoneInfoPhoneCall.companyIsZoned) {
    // company wasn't zoned at time of the call, ignore other zoneInfo state
    return { noZoneReason: undefined, selectedZone: undefined };
  } else if (!zoneInfoPhoneCall.companyZoneId) {
    // company was zoned, but no zone was selected, set noZoneReason
    return {
      noZoneReason: 'Information is not available because a Zone was not selected during the call.'
    };
  } else {
    // company was zoned, try and find zone or set noZoneReason if it's been deleted
    const selectedZone = (company?.zones || []).find((z) => z.id === zoneInfoPhoneCall.companyZoneId);
    return {
      selectedZone,
      // If !selectedZone, zone no longer exists on the company, set noZoneReason:
      noZoneReason: !selectedZone
        ? `Information is not available because the selected Zone (${phoneCall.companyZoneTitle}) no longer exists`
        : undefined
    };
  }
};

const ZoneDisplay = (props: { zone: Company['zones'][number]; gridItemSize: number }) => {
  const { zone, gridItemSize } = props;
  return (
    <Stack direction='column' spacing={2}>
      <KeyValueGrid
        gridItemSize={gridItemSize}
        keyValueArray={[
          { label: 'Title', textOrComponent: zone.title },
          { label: 'Zip Codes Serviced', textOrComponent: zone.zipCodes.join(', ') }
        ]}
      />
      <HeaderBox headerText='Dispatch Rules'>
        <ShowMoreText text={zone.callCenter.dispatchRules || 'N/A'} />
      </HeaderBox>
      <HeaderBox headerText='Big Ticket Items'>
        <ShowMoreText text={zone.callCenter.bigTicketItems || 'N/A'} />
      </HeaderBox>
      <HeaderBox headerText='Big Ticket Item Scheduling Rules'>
        <ShowMoreText text={zone.callCenter.bigTicketItemRules || 'N/A'} />
      </HeaderBox>
      <HeaderBox headerText='Warranty Rules'>
        <ShowMoreText text={zone.callCenter.warrantyRules || 'N/A'} />
      </HeaderBox>
      <HeaderBox headerText='Notes'>
        <ShowMoreText text={zone.callCenter.notes || 'N/A'} />
      </HeaderBox>
      <Stack direction='row' spacing={2} sx={{ justifyContent: 'flex-start' }}>
        <HeaderBox headerText='PRIMARY CONTACT' sx={{ width: '50%' }}>
          <FormattedContactBlock contact={zone.primaryContact} />
        </HeaderBox>
        <HeaderBox headerText='SECONDARY CONTACT' sx={{ width: '50%' }}>
          <FormattedContactBlock contact={zone.secondaryContact} />
        </HeaderBox>
      </Stack>
      <HeaderBox headerText='ON CALL'>
        <HeaderBox headerText='Instructions'>
          <Markup
            typographyProps={{ sx: { whiteSpace: 'pre-line', overflowWrap: 'break-word' } }}
            content={zone.onCallNotes || 'N/A'}
          />
        </HeaderBox>
      </HeaderBox>
    </Stack>
  );
};

export const PhoneCallCompanyTabs = (props: {
  companyData: Company;
  phoneCallDetailData: PhoneCallDetailDTO;
  gridItemSize: number;
}) => {
  const { companyData, phoneCallDetailData, gridItemSize } = props;
  const selectedZoneInfo = getZoneInfo(phoneCallDetailData, companyData);

  const [tabIndex, setTabIndex] = useState(0);

  const [zoneZipSelectState, setZoneZipSelectState] = useImmer({
    ...createZoneZipSelectState(),
    label: companyData.isZoned ? 'Selected Zone' : 'Selected Zip Code',
    labelHighlight: companyData.isZoned ? '(No Zone Selected)' : '(No Zip Code Selected)'
  });
  const zoneZipOptions = getZoneZipOptions(companyData);
  const setSelectedOption = (opt: AutoSelectOption | null) => {
    setZoneZipSelectState((state) => {
      state.selectedOption = opt;
      state.searchResultsOpen = opt ? false : state.searchResultsOpen;
    });

    if (companyData.isZoned) {
      setZoneZipSelectState((state) => {
        state.label = opt?.zone ? `Selected Zone (${opt?.zone.title})` : 'Selected Zone';
        state.labelHighlight = opt?.zone ? '' : '(No Zone Selected)';
      });
    } else {
      setZoneZipSelectState((state) => {
        state.label = 'Selected Zip Code';
        state.labelHighlight = opt?.zipCode ? '' : '(No Zip Code Selected)';
      });
    }
  };

  return (
    <>
      <Tabs
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
        value={tabIndex}
        onChange={(_event, newValue: number) => setTabIndex(newValue)}>
        <Tab key={0} label='CALL INFO' />
        <Tab key={1} label='NOTES' />
        <Tab key={2} label='CALL ZONE' sx={{ display: companyData.isZoned ? undefined : 'none' }} />
        <Tab key={3} label={`${companyData.isZoned ? 'ZONE' : 'ZIP'} SEARCH`} />
      </Tabs>
      <Divider />
      <TabPanel currentIndex={tabIndex} index={0}>
        <Stack useFlexGap spacing={2} sx={{ padding: 2, width: '100%' }}>
          {!phoneCallDetailData.companySwitched && <HeaderTextBlock headerText='Name' text={companyData.name} />}
          <KeyValueGrid
            gridItemSize={gridItemSize}
            keyValueArray={[
              {
                label: 'DNIS',
                textOrComponent: (
                  <IconTypography
                    startIcon={<PhoneIcon color='primary' />}
                    text={formatPhone(phoneCallDetailData.companyDNIS)}
                  />
                )
              },
              {
                label: 'Trades',
                textOrComponent: <TradesDisplay trades={companyData.callCenter.trades} color='primary' />
              }
            ]}
          />
          {phoneCallDetailData.companySwitched && (
            <>
              <HeaderTextBlock headerText='Originated From' text={phoneCallDetailData.originalCompany} />
              <HeaderTextBlock headerText='Switched To' text={companyData.name} />
            </>
          )}
          {!!phoneCallDetailData.toCompanyEmails?.length && (
            <HeaderBox headerText='To Email'>
              <IconTypography
                startIcon={<EmailIcon color='primary' />}
                text={phoneCallDetailData.toCompanyEmails.join(', ')}
              />
            </HeaderBox>
          )}
          {!!phoneCallDetailData.toCompanyCCEmails?.length && (
            <HeaderBox headerText='CC Email'>
              <IconTypography
                startIcon={<EmailIcon color='primary' />}
                text={phoneCallDetailData.toCompanyCCEmails.join(', ')}
              />
            </HeaderBox>
          )}
          {!!phoneCallDetailData.toCompanyBCCEmails?.length && (
            <HeaderBox headerText='BCC Email'>
              <IconTypography
                startIcon={<EmailIcon color='primary' />}
                text={phoneCallDetailData.toCompanyBCCEmails.join(', ')}
              />
            </HeaderBox>
          )}
          {!!phoneCallDetailData.toCompanyPlainTextEmails?.length && (
            <HeaderBox headerText='Plain Text Email'>
              <IconTypography
                startIcon={<EmailIcon color='primary' />}
                text={phoneCallDetailData.toCompanyPlainTextEmails.join(', ')}
              />
            </HeaderBox>
          )}
        </Stack>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={1} sx={{ overflow: 'auto', maxHeight: '500px', marginBottom: 1 }}>
        <Stack useFlexGap spacing={2} sx={{ padding: 2, width: '100%' }}>
          <HeaderBox headerText='Work/Services NOT to Book'>
            <TextBlockTypography
              sx={(theme) => getLeftHighlightWarnSx(theme)}
              text={companyData.callCenter.workNotToBookRules}
            />
          </HeaderBox>
          <HeaderBox headerText='Work/Services to Book'>
            <TextBlockTypography
              sx={(theme) => getLeftHighlightOkSx(theme)}
              text={companyData.callCenter.workToBookRules}
            />
          </HeaderBox>
          <HeaderTextBlock headerText='Dispatch Rules' text={companyData.callCenter.dispatchRules || 'N/A'} />
          <HeaderTextBlock headerText='Warranty Rules' text={companyData.callCenter.warrantyRules || 'N/A'} />
          <HeaderTextBlock headerText='General Notes' text={companyData.callCenter.notes || 'N/A'} />
          <Typography component='p' sx={(theme) => getBoxStyleForCanIncludeFee(companyData, theme)}>
            {'Appointment fee '}
            <span style={{ fontWeight: 'bold' }}>{canOrCant(companyData.callCenter.canIncludeFee)}</span>
            {' be included with work'}
          </Typography>
          <Typography component='p' sx={(theme) => getBoxStyleForCanWaiveFee(companyData, theme)}>
            {'Appointment fee '}
            <span style={{ fontWeight: 'bold' }}>{canOrCant(companyData.callCenter.canWaiveFee)}</span>
            {' be waived'}
          </Typography>
          {!companyData.isZoned && (
            <HeaderTextBlock
              headerText='Zip Codes Serviced'
              text={companyData.callCenter.zipCodesServiced.join(', ')}
            />
          )}
        </Stack>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={2} sx={{ overflow: 'auto', maxHeight: '500px' }}>
        <Stack useFlexGap spacing={2} sx={{ padding: 2, width: '100%' }}>
          {!!selectedZoneInfo.selectedZone && (
            <FlexBox sx={{ paddingX: 1, paddingBottom: 1, flexDirection: 'column' }}>
              <ZoneDisplay zone={selectedZoneInfo.selectedZone} gridItemSize={gridItemSize} />
            </FlexBox>
          )}
          {(!!selectedZoneInfo.noZoneReason || !selectedZoneInfo.selectedZone) && (
            <WarningAlert sx={{ justifyContent: 'center' }}>
              <Typography>
                {selectedZoneInfo.noZoneReason ||
                  'Information is not available because a Zone was not selected during the call.'}
              </Typography>
            </WarningAlert>
          )}
        </Stack>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={3} sx={{ overflow: 'auto', maxHeight: '500px' }}>
        <Stack useFlexGap spacing={2} sx={{ padding: 1, width: '100%' }}>
          <FlexBox
            sx={{
              width: '100%',
              flexDirection: 'column',
              borderRadius: 2,
              padding: 2
            }}>
            <Autocomplete
              disabled={zoneZipSelectState.disabled}
              filterOptions={zoneZipFilterOptions}
              freeSolo={true}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.zoneTitle || option.zipCode)}
              inputMode='search'
              inputValue={zoneZipSelectState.search}
              isOptionEqualToValue={(option, value) =>
                (option.zoneTitle && option.zoneTitle === value.zoneTitle) || option.zipCode === value.zipCode
              }
              onChange={(_e, value, reason) => {
                if (reason === 'selectOption' && value && typeof value !== 'string') {
                  setSelectedOption(value);
                } else if (reason === 'clear') {
                  setSelectedOption(null);
                }
              }}
              onInputChange={(_e, searchText, _reason) => {
                setZoneZipSelectState((state) => {
                  state.search = searchText;
                });

                if (companyData.isZoned) {
                  const exactMatch = searchText && findZoneZipMatch(searchText, zoneZipOptions, companyData.isZoned);
                  setSelectedOption(exactMatch || null);
                } else {
                  const isValid = !!searchText && ZIP_CODE_REGEX.test(searchText);
                  setZoneZipSelectState((state) => {
                    state.searchError = !isValid;
                  });
                  if (isValid) {
                    const exactMatch = findZoneZipMatch(searchText, zoneZipOptions, companyData.isZoned);
                    if (exactMatch) {
                      setSelectedOption(exactMatch);
                    }
                  }
                }
              }}
              open={zoneZipSelectState.searchResultsOpen}
              onOpen={() =>
                setZoneZipSelectState((state) => {
                  state.searchResultsOpen = true;
                })
              }
              onClose={() =>
                setZoneZipSelectState((state) => {
                  state.searchResultsOpen = false;
                })
              }
              options={zoneZipOptions}
              size='small'
              value={zoneZipSelectState.selectedOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={zoneZipSelectState.label}
                  error={!companyData.isZoned && zoneZipSelectState.searchError}
                  helperText={zoneZipSelectState.labelHighlight}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      // if Company is NOT Zoned, maxLength is 5 characters
                      maxLength: !companyData.isZoned ? 5 : undefined
                    }
                  }}
                />
              )}
              renderOption={(props, option: AutoSelectOption) => (
                <li {...props}>
                  {option.zoneTitle && <Typography variant='body1Bold' text={option.zoneTitle} />}
                  {!option.zoneTitle && <Typography text={option.zipCode} />}
                  {!option.zoneTitle && option.zone && (
                    <>
                      &nbsp;
                      <Typography variant='body1Bold' text={option.zone.title} />
                    </>
                  )}
                </li>
              )}
            />
          </FlexBox>
          {(zoneZipSelectState.selectedOption || !companyData.isZoned) && <Divider />}
          {companyData.isZoned && zoneZipSelectState.selectedOption?.zone && (
            <FlexBox sx={{ paddingX: 2, paddingBottom: 1, flexDirection: 'column' }}>
              <ZoneDisplay gridItemSize={gridItemSize} zone={zoneZipSelectState.selectedOption.zone} />
            </FlexBox>
          )}
          {!companyData.isZoned && (
            <HeaderTextBlock
              sx={{ padding: 2 }}
              headerText='Zip Codes Serviced'
              text={companyData.callCenter.zipCodesServiced.join(', ')}
            />
          )}
        </Stack>
      </TabPanel>
    </>
  );
};
